// Composition d'une adresse pour l'AFFICHAGE. Le stockage ne change pas : la base garde numero_voie,
// type_voie, nom_voie, code_postal, ville et pays separement. Ces fonctions decident seulement de ce
// que l'utilisateur lit.

/**
 * `numero_voie` porte un 0 sentinelle sur pres d'un millier d'adresses en base (938 au 21/07/2026,
 * de loin la valeur la plus frequente). Il signifie « pas de numero » — une place, un lieu-dit — et
 * non le numero zero, qui n'existe pas dans l'adressage francais. L'afficher produit « 0 Place
 * Barette », qui se lit comme une donnee fausse.
 *
 * Un 0 SUIVI d'un complement (`0 bis`, `0ter`) est conserve : c'est le complement qui porte alors
 * l'information, et le retirer perdrait une distinction reelle entre deux adresses voisines.
 */
const NUMERO_VOIE_SENTINELLE = /^0+$/;

export const numeroVoieAffiche = (numeroVoie?: string | number | null): string => {
  const valeur = String(numeroVoie ?? '').trim();
  return NUMERO_VOIE_SENTINELLE.test(valeur) ? '' : valeur;
};

/** Types de voie reconnus, pour separer « Rue d'Athenes » en type + nom. Minuscules et sans accent
 *  a la comparaison ; la casse d'origine est conservee dans ce qui est ecrit. */
const TYPES_VOIE = [
  'rue', 'avenue', 'boulevard', 'impasse', 'allee', 'place', 'chemin', 'route', 'quai', 'cours',
  'square', 'esplanade', 'passage', 'sentier', 'villa', 'promenade', 'rond-point', 'voie',
];

// \u0300-\u036f = marques diacritiques combinantes isolees par NFD (l'accent devient un caractere a
// part). Notation echappee et non litterale : ces caracteres sont invisibles dans un editeur.
const sansAccent = (valeur: string): string => valeur.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Separe le type de voie du nom : « Rue d'Athenes » -> { typeVoie: 'Rue', nomVoie: "d'Athenes" }.
 *
 * L'API IGN renvoie `street` type inclus. L'ecrire tel quel dans `nom_voie` sans toucher `type_voie`
 * laisse l'ancien type en place, et toute composition affiche alors le type deux fois
 * (« 8 Rue Rue d'Athenes »). La casse d'origine est conservee : la base contient deja des `Rue` et
 * des `rue`, en normaliser un seul cote creerait une troisieme variante.
 */
export const separerTypeVoie = (street?: string | null): { typeVoie: string; nomVoie: string } => {
  const valeur = String(street ?? '').trim();
  if (valeur === '') return { typeVoie: '', nomVoie: '' };
  const premierMot = valeur.split(/\s+/)[0];
  if (!TYPES_VOIE.includes(sansAccent(premierMot).toLowerCase())) {
    return { typeVoie: '', nomVoie: valeur };
  }
  return { typeVoie: premierMot, nomVoie: valeur.slice(premierMot.length).trim() };
};

/**
 * Voie seule : « 12 Rue des Lilas ». Le numero sentinelle est retire (cf. numeroVoieAffiche).
 *
 * Le type de voie n'est pas repete quand `nom_voie` commence deja par lui : 149 adresses portent le
 * type aux deux endroits (`type_voie` = « Rue », `nom_voie` = « Rue d'Athenes »), heritage d'un import
 * qui posait « Rue » par defaut combine a une saisie qui ecrivait le `street` IGN brut. Dedoublonner
 * a l'affichage repare ces lignes sans migration ; `separerTypeVoie` empeche d'en creer de nouvelles.
 */
export const ligneVoie = (
  numeroVoie?: string | number | null,
  typeVoie?: string | null,
  nomVoie?: string | null,
): string => {
  const nom = String(nomVoie ?? '').trim();
  const type = String(typeVoie ?? '').trim();
  const typeDejaDansNom =
    type !== '' && sansAccent(nom).toLowerCase().startsWith(`${sansAccent(type).toLowerCase()} `);
  return [numeroVoieAffiche(numeroVoie), typeDejaDansNom ? '' : type, nom].filter(Boolean).join(' ');
};

/**
 * Adresse complete sur UNE ligne : « 12 Rue des Lilas, 75001 Paris, France ».
 *
 * Les saisies passent desormais par un seul champ (la suggestion IGN renseigne code postal, ville et
 * pays sans les montrer) ; en lecture seule on recompose la ligne entiere, sinon on ne lirait que la
 * voie et l'adresse perdrait sa localisation. Chaque segment est filtre : une fiche ancienne peut
 * n'avoir que la voie, la ligne se contracte au lieu d'afficher des virgules orphelines.
 */
export const adresseUneLigne = (
  voie?: string | null,
  codePostal?: string | null,
  ville?: string | null,
  pays?: string | null,
): string =>
  [voie, [codePostal, ville].map((p) => String(p ?? '').trim()).filter(Boolean).join(' '), pays]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(', ');

/** Raccourci pour une entite `adresse` telle que renvoyee par l'API (voie eclatee en trois colonnes). */
export const adresseEntiteUneLigne = (adresse?: {
  numero_voie?: string | number | null;
  type_voie?: string | null;
  nom_voie?: string | null;
  code_postal?: string | null;
  ville?: string | null;
  pays?: string | null;
} | null): string =>
  adresseUneLigne(
    ligneVoie(adresse?.numero_voie, adresse?.type_voie, adresse?.nom_voie),
    adresse?.code_postal,
    adresse?.ville,
    adresse?.pays,
  );

/**
 * Pays du siege social, code ISO 3166-1 alpha-2. Cette liste sert le formulaire d'inscription
 * prestataire ET la fiche entreprise du profil : les deux ecrivent la meme colonne
 * adresses.pays, une liste par ecran les ferait diverger.
 *
 * Elle n'est volontairement PAS restreinte aux pays autorises a candidater. Ce perimetre
 * n'existe pas dans le modele Centralis : il est fixe par le reglement de consultation de
 * chaque accord-cadre. Le figer ici transformerait une decision juridique en constante front,
 * et chaque cas limite (Monaco, Suisse, Royaume-Uni, filiale d'un groupe non europeen)
 * reviendrait la rouvrir.
 *
 * L'Europe est presentee en premier parce qu'elle couvre la quasi-totalite des cas reels ; le
 * reste du monde suit, pour ne bloquer personne.
 */
export type OptionPays = { code: string; nom: string };

export const PAYS_EUROPE: OptionPays[] = [
  { code: "AL", nom: "Albanie" },
  { code: "DE", nom: "Allemagne" },
  { code: "AD", nom: "Andorre" },
  { code: "AM", nom: "Arménie" },
  { code: "AT", nom: "Autriche" },
  { code: "AZ", nom: "Azerbaïdjan" },
  { code: "BE", nom: "Belgique" },
  { code: "BY", nom: "Biélorussie" },
  { code: "BA", nom: "Bosnie-Herzégovine" },
  { code: "BG", nom: "Bulgarie" },
  { code: "CY", nom: "Chypre" },
  { code: "HR", nom: "Croatie" },
  { code: "DK", nom: "Danemark" },
  { code: "ES", nom: "Espagne" },
  { code: "EE", nom: "Estonie" },
  { code: "VA", nom: "État de la Cité du Vatican" },
  { code: "FI", nom: "Finlande" },
  { code: "FR", nom: "France" },
  { code: "GE", nom: "Géorgie" },
  { code: "GR", nom: "Grèce" },
  { code: "HU", nom: "Hongrie" },
  { code: "IE", nom: "Irlande" },
  { code: "IS", nom: "Islande" },
  { code: "IT", nom: "Italie" },
  { code: "LV", nom: "Lettonie" },
  { code: "LI", nom: "Liechtenstein" },
  { code: "LT", nom: "Lituanie" },
  { code: "LU", nom: "Luxembourg" },
  { code: "MK", nom: "Macédoine du Nord" },
  { code: "MT", nom: "Malte" },
  { code: "MD", nom: "Moldavie" },
  { code: "MC", nom: "Monaco" },
  { code: "ME", nom: "Monténégro" },
  { code: "NO", nom: "Norvège" },
  { code: "NL", nom: "Pays-Bas" },
  { code: "PL", nom: "Pologne" },
  { code: "PT", nom: "Portugal" },
  { code: "RO", nom: "Roumanie" },
  { code: "GB", nom: "Royaume-Uni" },
  { code: "RU", nom: "Russie" },
  { code: "SM", nom: "Saint-Marin" },
  { code: "RS", nom: "Serbie" },
  { code: "SK", nom: "Slovaquie" },
  { code: "SI", nom: "Slovénie" },
  { code: "SE", nom: "Suède" },
  { code: "CH", nom: "Suisse" },
  { code: "CZ", nom: "Tchéquie" },
  { code: "TR", nom: "Turquie" },
  { code: "UA", nom: "Ukraine" },
];

export const PAYS_HORS_EUROPE: OptionPays[] = [
  { code: "AF", nom: "Afghanistan" },
  { code: "ZA", nom: "Afrique du Sud" },
  { code: "DZ", nom: "Algérie" },
  { code: "AO", nom: "Angola" },
  { code: "AI", nom: "Anguilla" },
  { code: "AG", nom: "Antigua-et-Barbuda" },
  { code: "SA", nom: "Arabie saoudite" },
  { code: "AR", nom: "Argentine" },
  { code: "AW", nom: "Aruba" },
  { code: "AU", nom: "Australie" },
  { code: "BS", nom: "Bahamas" },
  { code: "BH", nom: "Bahreïn" },
  { code: "BD", nom: "Bangladesh" },
  { code: "BB", nom: "Barbade" },
  { code: "BZ", nom: "Belize" },
  { code: "BJ", nom: "Bénin" },
  { code: "DY", nom: "Bénin" },
  { code: "BM", nom: "Bermudes" },
  { code: "BT", nom: "Bhoutan" },
  { code: "BO", nom: "Bolivie" },
  { code: "BW", nom: "Botswana" },
  { code: "BR", nom: "Brésil" },
  { code: "BN", nom: "Brunei" },
  { code: "BF", nom: "Burkina Faso" },
  { code: "HV", nom: "Burkina Faso" },
  { code: "BI", nom: "Burundi" },
  { code: "KH", nom: "Cambodge" },
  { code: "CM", nom: "Cameroun" },
  { code: "CA", nom: "Canada" },
  { code: "CV", nom: "Cap-Vert" },
  { code: "CL", nom: "Chili" },
  { code: "CN", nom: "Chine" },
  { code: "CO", nom: "Colombie" },
  { code: "KM", nom: "Comores" },
  { code: "CG", nom: "Congo-Brazzaville" },
  { code: "CD", nom: "Congo-Kinshasa" },
  { code: "KP", nom: "Corée du Nord" },
  { code: "KR", nom: "Corée du Sud" },
  { code: "CR", nom: "Costa Rica" },
  { code: "CI", nom: "Côte d’Ivoire" },
  { code: "CU", nom: "Cuba" },
  { code: "CW", nom: "Curaçao" },
  { code: "DJ", nom: "Djibouti" },
  { code: "DM", nom: "Dominique" },
  { code: "EG", nom: "Égypte" },
  { code: "AE", nom: "Émirats arabes unis" },
  { code: "EC", nom: "Équateur" },
  { code: "ER", nom: "Érythrée" },
  { code: "SZ", nom: "Eswatini" },
  { code: "US", nom: "États-Unis" },
  { code: "ET", nom: "Éthiopie" },
  { code: "FJ", nom: "Fidji" },
  { code: "GA", nom: "Gabon" },
  { code: "GM", nom: "Gambie" },
  { code: "GH", nom: "Ghana" },
  { code: "GI", nom: "Gibraltar" },
  { code: "GD", nom: "Grenade" },
  { code: "GL", nom: "Groenland" },
  { code: "GP", nom: "Guadeloupe" },
  { code: "GU", nom: "Guam" },
  { code: "GT", nom: "Guatemala" },
  { code: "GG", nom: "Guernesey" },
  { code: "GN", nom: "Guinée" },
  { code: "GQ", nom: "Guinée équatoriale" },
  { code: "GW", nom: "Guinée-Bissau" },
  { code: "GY", nom: "Guyana" },
  { code: "GF", nom: "Guyane française" },
  { code: "HT", nom: "Haïti" },
  { code: "HN", nom: "Honduras" },
  { code: "CX", nom: "Île Christmas" },
  { code: "IM", nom: "Île de Man" },
  { code: "NF", nom: "Île Norfolk" },
  { code: "KY", nom: "Îles Caïmans" },
  { code: "CC", nom: "Îles Cocos" },
  { code: "CK", nom: "Îles Cook" },
  { code: "FO", nom: "Îles Féroé" },
  { code: "FK", nom: "Îles Malouines" },
  { code: "MP", nom: "Îles Mariannes du Nord" },
  { code: "MH", nom: "Îles Marshall" },
  { code: "SB", nom: "Îles Salomon" },
  { code: "TC", nom: "Îles Turques-et-Caïques" },
  { code: "VG", nom: "Îles Vierges britanniques" },
  { code: "VI", nom: "Îles Vierges des États-Unis" },
  { code: "IN", nom: "Inde" },
  { code: "ID", nom: "Indonésie" },
  { code: "IQ", nom: "Irak" },
  { code: "IR", nom: "Iran" },
  { code: "IL", nom: "Israël" },
  { code: "JM", nom: "Jamaïque" },
  { code: "JP", nom: "Japon" },
  { code: "JE", nom: "Jersey" },
  { code: "JO", nom: "Jordanie" },
  { code: "KZ", nom: "Kazakhstan" },
  { code: "KE", nom: "Kenya" },
  { code: "KG", nom: "Kirghizstan" },
  { code: "KI", nom: "Kiribati" },
  { code: "KW", nom: "Koweït" },
  { code: "RE", nom: "La Réunion" },
  { code: "LA", nom: "Laos" },
  { code: "LS", nom: "Lesotho" },
  { code: "LB", nom: "Liban" },
  { code: "LR", nom: "Liberia" },
  { code: "LY", nom: "Libye" },
  { code: "MG", nom: "Madagascar" },
  { code: "MY", nom: "Malaisie" },
  { code: "MW", nom: "Malawi" },
  { code: "MV", nom: "Maldives" },
  { code: "ML", nom: "Mali" },
  { code: "MA", nom: "Maroc" },
  { code: "MQ", nom: "Martinique" },
  { code: "MU", nom: "Maurice" },
  { code: "MR", nom: "Mauritanie" },
  { code: "YT", nom: "Mayotte" },
  { code: "MX", nom: "Mexique" },
  { code: "FM", nom: "Micronésie" },
  { code: "MN", nom: "Mongolie" },
  { code: "MS", nom: "Montserrat" },
  { code: "MZ", nom: "Mozambique" },
  { code: "MM", nom: "Myanmar (Birmanie)" },
  { code: "NA", nom: "Namibie" },
  { code: "NR", nom: "Nauru" },
  { code: "NP", nom: "Népal" },
  { code: "NI", nom: "Nicaragua" },
  { code: "NE", nom: "Niger" },
  { code: "NG", nom: "Nigeria" },
  { code: "NU", nom: "Niue" },
  { code: "NC", nom: "Nouvelle-Calédonie" },
  { code: "NZ", nom: "Nouvelle-Zélande" },
  { code: "OM", nom: "Oman" },
  { code: "UG", nom: "Ouganda" },
  { code: "UZ", nom: "Ouzbékistan" },
  { code: "PK", nom: "Pakistan" },
  { code: "PW", nom: "Palaos" },
  { code: "PA", nom: "Panama" },
  { code: "PG", nom: "Papouasie-Nouvelle-Guinée" },
  { code: "PY", nom: "Paraguay" },
  { code: "BQ", nom: "Pays-Bas caribéens" },
  { code: "PE", nom: "Pérou" },
  { code: "PH", nom: "Philippines" },
  { code: "PF", nom: "Polynésie française" },
  { code: "PR", nom: "Porto Rico" },
  { code: "QA", nom: "Qatar" },
  { code: "HK", nom: "R.A.S. chinoise de Hong Kong" },
  { code: "MO", nom: "R.A.S. chinoise de Macao" },
  { code: "ZZ", nom: "région indéterminée" },
  { code: "CF", nom: "République centrafricaine" },
  { code: "DO", nom: "République dominicaine" },
  { code: "UK", nom: "Royaume-Uni" },
  { code: "RW", nom: "Rwanda" },
  { code: "BL", nom: "Saint-Barthélemy" },
  { code: "KN", nom: "Saint-Christophe-et-Niévès" },
  { code: "MF", nom: "Saint-Martin" },
  { code: "SX", nom: "Saint-Martin (partie néerlandaise)" },
  { code: "PM", nom: "Saint-Pierre-et-Miquelon" },
  { code: "VC", nom: "Saint-Vincent-et-les Grenadines" },
  { code: "SH", nom: "Sainte-Hélène" },
  { code: "LC", nom: "Sainte-Lucie" },
  { code: "SV", nom: "Salvador" },
  { code: "WS", nom: "Samoa" },
  { code: "AS", nom: "Samoa américaines" },
  { code: "ST", nom: "Sao Tomé-et-Principe" },
  { code: "SN", nom: "Sénégal" },
  { code: "SC", nom: "Seychelles" },
  { code: "SL", nom: "Sierra Leone" },
  { code: "SG", nom: "Singapour" },
  { code: "SO", nom: "Somalie" },
  { code: "SD", nom: "Soudan" },
  { code: "SS", nom: "Soudan du Sud" },
  { code: "LK", nom: "Sri Lanka" },
  { code: "SR", nom: "Suriname" },
  { code: "SY", nom: "Syrie" },
  { code: "TJ", nom: "Tadjikistan" },
  { code: "TW", nom: "Taïwan" },
  { code: "TZ", nom: "Tanzanie" },
  { code: "TD", nom: "Tchad" },
  { code: "PS", nom: "Territoires palestiniens" },
  { code: "TH", nom: "Thaïlande" },
  { code: "TL", nom: "Timor oriental" },
  { code: "TP", nom: "Timor oriental" },
  { code: "TG", nom: "Togo" },
  { code: "TK", nom: "Tokelau" },
  { code: "TO", nom: "Tonga" },
  { code: "TT", nom: "Trinité-et-Tobago" },
  { code: "TN", nom: "Tunisie" },
  { code: "TM", nom: "Turkménistan" },
  { code: "TV", nom: "Tuvalu" },
  { code: "UY", nom: "Uruguay" },
  { code: "NH", nom: "Vanuatu" },
  { code: "VU", nom: "Vanuatu" },
  { code: "VE", nom: "Venezuela" },
  { code: "VD", nom: "Viêt Nam" },
  { code: "VN", nom: "Viêt Nam" },
  { code: "WF", nom: "Wallis-et-Futuna" },
  { code: "YD", nom: "Yémen" },
  { code: "YE", nom: "Yémen" },
  { code: "ZM", nom: "Zambie" },
  { code: "RH", nom: "Zimbabwe" },
  { code: "ZW", nom: "Zimbabwe" },
];

/**
 * Codes reconnus comme la France. Miroir front de App\Support\Pays cote serveur : les deux
 * doivent repondre pareil, sinon le front laisse saisir ce que le serveur refuse en 422.
 * Une valeur absente vaut France -- c'est l'etat des adresses anterieures a la saisie du pays,
 * auxquelles les controles francais s'appliquaient deja.
 */
const VALEURS_FRANCE = ['FR', 'FRA', 'FRANCE', ''];

export const estFrance = (pays?: string | null): boolean =>
  VALEURS_FRANCE.includes(
    String(pays ?? '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase(),
  );

/**
 * Libelle affichable d'un code pays. Rend la valeur telle quelle si ce n'est pas un code connu :
 * les adresses acheteur stockent le libelle "France", pas un code.
 */
export const nomPays = (code?: string | null): string => {
  const valeur = String(code ?? '').trim();
  if (valeur === '') return '';
  const trouve = [...PAYS_EUROPE, ...PAYS_HORS_EUROPE].find((p) => p.code === valeur.toUpperCase());
  return trouve ? trouve.nom : valeur;
};

/** Adresse telle que saisie dans un formulaire : la voie eclatee comme en base. */
export interface AdresseSaisie {
  numero_voie: string;
  type_voie: string;
  nom_voie: string;
  code_postal: string;
  ville: string;
}

/** Les trois champs sans lesquels une adresse n'est pas enregistrable ; le numero est facultatif. */
export const adresseSaisieComplete = (adresse: AdresseSaisie): boolean =>
  [adresse.nom_voie, adresse.code_postal, adresse.ville].every((champ) => champ.trim() !== '');
